// Constants
import { DriveIdParamName, ItemIdParamName, SitePathParamName } from "@ms/uno-constants/lib/local/LoopConstants";
import { CopilotFolderName, PlannerFolderName, StatusReportsFolderName } from "@ms/uno-constants/lib/local/UrlConstants";
// Errors
import { ArgumentNullError } from "@ms/uno-errors/lib/local/errors/ArgumentNullError";
// Utilities
import first from "lodash/first";
import last from "lodash/last";
import { generateValidFilename } from "./FileUploadUtilities";
import { computeSha256Hash } from "./HashUtilities";
import Uri from "./Uri";
const HttpScheme = "http";
const HttpsScheme = "https";
const AllowedEUPIQueryParamsRegex = /^(page|isFullScreen|auth_pvr|tid|tenantId|userObjectId|planId|tasksAppFlavor|subEntityId|entityId|sessionId|groupId|channelId|theme|mkt|locale|ringId|PlannerRouteHint)$/i;
// Regular expression to find URLs in the message
const UrlRegex = /https?:\/\/[^\s'"]+/g;
/**
 * Ensures that the url string starts with http or https.
 * If the URL scheme is already http, it will be formatted with http scheme. Otherwise the url will be formatted with https scheme.
 * @param url The Url to format with http/https scheme if needed
 */ export function formatUrlWithHttpUrlScheme(url) {
    return getUrlScheme(url).toLowerCase() === HttpScheme ? ensureUrlScheme(url, HttpScheme) : ensureUrlScheme(url, HttpsScheme);
}
/**
 * Ensures that string starts with designated scheme
 * @param url The Url to add scheme to
 * @param scheme The scheme to use
 */ export function ensureUrlScheme(url, scheme) {
    const justScheme = scheme && first(scheme.split("://"));
    return `${justScheme}://${removeUrlScheme(url)}`;
}
/**
 * Remove http/https prefix from string (does not mutate string)
 * @param url Url to extract non-http(s) string from
 */ export function removeUrlScheme(url) {
    // eslint-disable-next-line security/detect-unsafe-regex
    const httpsRegex = new RegExp("(http(s)?(:)?(//)?)*(.*)", "i");
    const matches = httpsRegex.exec(url) || [];
    return last(matches) || "";
}
/**
 * Get URL scheme from string, if any (does not mutate string)
 * @param url Url to extract the url scheme from
 */ export function getUrlScheme(url) {
    const httpsRegex = new RegExp("(.*)://.*", "i");
    const matches = httpsRegex.exec(url) || [];
    return last(matches) || "";
}
/**
 * Tries to construct a URL object. Assumes the url includes http(s)://
 * @param url Url to be tested
 * @param disableExtraHostnameValidation If true, skip the isValidUrlHostname check
 */ export function validateUrl(url, disableExtraHostnameValidation) {
    try {
        const urlObj = new URL(url);
        if (!urlObj.host || !urlObj.hostname || !urlObj.origin || urlObj.origin === "null" || urlObj.protocol !== "http:" && urlObj.protocol !== "https:" || !disableExtraHostnameValidation && !isValidUrlHostname(urlObj)) {
            return;
        }
        return urlObj.toString();
    } catch  {
        return;
    }
}
/**
 * Generate the fav-icon url for a domain from its url
 * @param url URL of the domain for which we want the fav-icon link
 */ export function getFavIconUrl(url) {
    return "https://" + extractHostname(url) + "/favicon.ico";
}
/**
 * Formats and encodes the URL and only returns if the URL is a valid one, otherwise returns undefined
 * @param url Url to format and encode
 * @param disableExtraHostnameValidation If true, skip the isValidUrlHostname check
 */ export function getValidUrl(url, disableExtraHostnameValidation) {
    const formattedUrl = formatUrlWithHttpUrlScheme(url);
    return validateUrl(formattedUrl, disableExtraHostnameValidation);
}
/**
 * Validates if the given URL is valid or not
 * @param url Url to validate
 * @param disableExtraHostnameValidation If true, skip the isValidUrlHostname check
 */ export function isValidURL(url, disableExtraHostnameValidation) {
    const formattedUrl = formatUrlWithHttpUrlScheme(url);
    return !!validateUrl(formattedUrl, disableExtraHostnameValidation);
}
/**
 * Extract hostname from a string
 * @param url URL string to extract hostname from
 */ export function extractHostname(url) {
    let hostname = "";
    // Find & remove protocol (http, ftp, etc.) and get hostname
    if (url.indexOf("://") > -1) {
        hostname = url.split("/")[2];
    } else {
        hostname = url.split("/")[0];
    }
    // Find & remove port number
    hostname = hostname.split(":")[0];
    // Find & remove "?"
    hostname = hostname.split("?")[0];
    // Find & remove "#"
    hostname = hostname.split("#")[0];
    return hostname;
}
/**
 * Redacts URLs in error messages by replacing them with just their hostnames to remove PII
 * @param message - The error message that may contain URLs with PII
 * @returns The error message with URLs replaced by hostnames only
 */ export function redactUrlsToHostnames(message) {
    if (!message || typeof message !== "string") {
        return "[Unknown error]";
    }
    return message.replace(UrlRegex, (url)=>{
        // Extract just the hostname from the URL to remove all PII
        return extractHostname(url);
    });
}
/**
 * Get the value of a query param from a url
 * @param url Url to extract query param from
 * @param queryParamName Name of the query param to extract
 * @returns Value of the query param
 */ export function getQueryParamValue(url, queryParamName) {
    const params = new URLSearchParams(new URL(url).search);
    return params.get(queryParamName);
}
/**
 * Remove the given query param from a url and return the updated url
 * @param urlString Url to remove query param from
 * @param queryParamName Name of the query param to remove
 * @returns Updated url without the query param
 */ export function removeQueryParam(urlString, queryParamName) {
    const url = new URL(urlString);
    url.searchParams.delete(queryParamName);
    return url.toString();
}
/**
 * Converts an object of key-value pairs into a query string
 * @param obj The object containing key-value pairs to convert
 * @param obj[key] The value for each query parameter
 * @param [urlEncode=true] Whether to URL-encode the values in the query string
 * @returns The generated query string
 */ export function urlParamsObjectToQueryString(obj, urlEncode = true) {
    return Object.keys(obj).reduce((queryParams, key)=>{
        const value = obj[key];
        // Skip undefined or null values
        if (typeof value === "undefined" || value === null) {
            return queryParams;
        }
        queryParams.push(key + "=" + (urlEncode ? encodeURIComponent(value) : value));
        return queryParams;
    }, []).join("&");
}
/**
 * Removes any EUPI containing query params from the url (URLs that return html for webpages)
 * @param urlString The URL string to remove EUPI query params from
 * @param allowedQueryParamsRegex The regex to match allowed query params. Defaults to the AllowedEUPIQueryParamsRegex
 * @return The URL string without EUPI query params
 */ export function removeEUPIQueryParams(urlString, allowedQueryParamsRegex = AllowedEUPIQueryParamsRegex) {
    if (!urlString || urlString.indexOf("?") === -1) return urlString;
    const url = new URL(urlString);
    const queryString = url.search;
    const searchParams = new URLSearchParams(queryString);
    // Collect keys to delete to avoid modifying collection during iteration
    const keysToDelete = [];
    for (const [key] of searchParams){
        if (!allowedQueryParamsRegex.test(key)) {
            keysToDelete.push(key);
        }
    }
    // Delete the disallowed keys
    keysToDelete.forEach((key)=>searchParams.delete(key));
    // Update the URL's search params
    url.search = searchParams.toString();
    return url.toString();
}
/**
 * Get the root url for the current user's tenant. This is used as a base for entity links.
 * @param plannerWebRootUrl The planner web root URL, typically "https://planner.cloud.microsoft"
 * @param userEmail The email of the user, used to determine the tenant domain
 */ export function getRedirectRootUrl(plannerWebRootUrl, userEmail) {
    const tenantDomain = getTenantDomain(userEmail);
    const tenantDomainPath = tenantDomain ? "/" + tenantDomain : "";
    const normalizedPlannerWebRootUrl = plannerWebRootUrl.endsWith("/") ? plannerWebRootUrl.slice(0, -1) : plannerWebRootUrl;
    return `${normalizedPlannerWebRootUrl}${tenantDomainPath}`;
}
/**
 * Some Project links have two sets of query parameters: one before the # character,
 * and one at the end according to URL standards. Accept a URL string and search both
 * query parameter sections for the specified query parameter.
 * @param urlString The URL string with non-standard query parameter sections
 * @param queryParams The query parameters to search for
 */ export function extractQueryParamsFromProjectUrl(urlString, queryParams) {
    const url = new URL(urlString);
    // searchParams will look at query params in the list at the end of the URL
    const searchParams = new URLSearchParams(url.hash.split("?")[1]);
    // The Project URL has a second list of query params before the hash. This is not standard URL formatting
    const searchParams2 = new URLSearchParams(url.href.split(/[#\?]/)[1]);
    const results = {};
    for (const queryParam of queryParams){
        results[queryParam] = searchParams.get(queryParam) ?? searchParams2.get(queryParam);
    }
    return results;
}
/**
 * Encodes a given share URL to a format suitable for ODSP to get the Shared Drive Item. The URL is base64 encoded and modified to be URL-safe.
 * https://learn.microsoft.com/en-us/graph/api/shares-get?view=graph-rest-1.0&tabs=http#encoding-sharing-urls
 * @param {string} shareUrl - The shareURL to be encoded.
 * @returns {string} The encoded shareURL in the format `u!<encodedshareUrl>`.
 */ export function encodeShareUrlForOdsp(shareUrl) {
    const encodedShareUrl = btoa(shareUrl).replace(/\//g, "_").replace(/\+/g, "-").replace(/=+$/, "");
    return `u!${encodedShareUrl}`;
}
/**
 * Parse tenant domain from user email
 * @param userEmail User email
 */ function getTenantDomain(userEmail) {
    if (!userEmail) {
        return "";
    }
    const emailParts = userEmail.split("@");
    return emailParts.length > 1 ? emailParts[1] : "";
}
export function getStatusReportLocationFromUrl(url, planId) {
    return url + `/${PlannerFolderName}/` + planId + `/${StatusReportsFolderName}`;
}
export function fromUtf8ToBase64(utf8String) {
    return btoa(unescape(encodeURIComponent(utf8String)));
}
/**
 * Resolves the ODSP data store locator from the Loop component URL.
 * @param {string} componentUrl - The URL of the Loop component to resolve.
 * @returns {IOdspDataStoreLocator} An object containing the siteUrl, driveId, and itemId.
 * @throws Error If the URL is invalid or the required parameters are missing.
 */ export const getOdspDataStoreLocator = (componentUrl)=>{
    let url;
    try {
        url = new URL(componentUrl);
    } catch  {
        throw new Error("Invalid URL");
    }
    // NOTE: No need to apply decodeURIComponent when accessing query params via URLSearchParams class.
    const encodedLocatorValue = url.searchParams.get("nav");
    if (!encodedLocatorValue) {
        throw new ArgumentNullError("nav");
    }
    const locatorInfo = new URLSearchParams(fromBase64ToUtf8(encodedLocatorValue));
    const sitePath = locatorInfo.get(SitePathParamName);
    const driveId = locatorInfo.get(DriveIdParamName);
    const itemId = locatorInfo.get(ItemIdParamName);
    // File storage locator params must be present and non-empty
    if (!sitePath) {
        throw new ArgumentNullError("sitePath");
    }
    if (!driveId) {
        throw new ArgumentNullError("driveId");
    }
    if (!itemId) {
        throw new ArgumentNullError("itemId");
    }
    return {
        siteUrl: url.origin + sitePath,
        driveId,
        itemId
    };
};
/**
 * Creates a SharePoint newsletter URL with the given storage URL, drive ID, and item ID.
 * @param siteUrl - The base URL of the SharePoint storage.
 * @param driveId - The ID of the drive containing the item.
 * @param itemId - The ID of the item to be included in the newsletter.
 * @returns The constructed SharePoint newsletter URL.
 */ export function createSharePointNewsletterUrl(siteUrl, driveId, itemId) {
    const baseUrl = `${siteUrl}/_layouts/15/DynamicPages.aspx`;
    const options = {
        mode: "mapping",
        scenario: "planner-report",
        sources: [
            {
                type: "item",
                driveId: driveId,
                itemId: itemId
            }
        ]
    };
    const encodedOptions = encodeURIComponent(JSON.stringify(options));
    return `${baseUrl}?options=${encodedOptions}`;
}
/**
 * Encode a Base64 string to a UTF-8 string
 * @param base64String - The Base64 encoded string
 * @returns The UTF-8 string
 */ export function fromBase64ToUtf8(base64String) {
    const binStr = atob(base64String);
    const arr = Uint8Array.from({
        length: binStr.length
    }, (_, i)=>binStr.charCodeAt(i));
    return new TextDecoder().decode(arr);
}
/**
 * Helper method to extract org url from external project url
 * @param externalProjectUrl external project url
 * @returns org url
 */ export function getOrgUrlFromExternalProjectUrl(externalProjectUrl) {
    // The generic structure of the external project url looks like the following:
    // Scenario 1 : The org url is part of the query params e.g.,  https://<host>/?org=<org>
    // Scenario 2 : The org url is part of the host e.g.,  https://<org>.crm.dynamics.com/
    // We check which of the case it is and then extract the org url accordingly
    const url = new Uri(externalProjectUrl);
    const queryParams = url.getQueryAsObject();
    const org = queryParams?.org ?? url.getHost();
    return `https://${org}/`;
}
/**
 * Ensures that the URL ends with a slash (/).
 * @param url The URL to ensure ends with a slash.
 * @returns The URL with a trailing slash.
 */ export function ensureTrailingSlash(url) {
    return url.endsWith("/") ? url : `${url}/`;
}
/**
 * Generates the content of an `.url` internet shortcut file for a given link.
 *
 * The returned content follows the standard format for internet shortcut files:
 * ```
 * [InternetShortcut]
 * URL=<link>
 * ```
 *
 * @param link - The URL to be included in the internet shortcut file.
 * @returns The formatted content of the internet shortcut file as a string.
 */ export const getInternetShortcutFileContentForLink = (link)=>{
    return `[InternetShortcut]\nURL=${link}`;
};
/**
 * Constructs the Copilot (PMA) folder path for a given plan ID in Microsoft Planner.
 * Note that for Premium Plans we need to pass the XRM ID of the plan.
 *
 * @param planXrmId - The ID of the plan.
 * @returns The folder path string in the format "Microsoft Planner/{planId}/Copilot".
 */ export function getCopilotFolderPath(planId) {
    return getPlanFolderPathFromPlanId(planId) + "/" + CopilotFolderName;
}
/**
 * Constructs the folder path for a given plan ID in Microsoft Planner.
 * Note that for Premium Plans we need to pass the XRM ID of the plan.
 *
 * @param planId - The ID of the plan.
 * @returns The folder path string in the format "Microsoft Planner/{planId}".
 */ export const getPlanFolderPathFromPlanId = (planId)=>{
    return `${PlannerFolderName}/` + planId;
};
/**
 * Validates hostname in specified URL is valid according to https://www.rfc-editor.org/rfc/rfc3986#section-3.2.2
 * Supports both domain names and IP addresses (IPv4/IPv6)
 * Note: This is so that we're more in line with .NET Uri (reduce chance of string accepted by JS URL and rejected by .NET Uri)
 * Side Note: We are actually a bit stricter than .NET Uri (which allows leading/trailing hyphens in labels) while RFC does not allow this
 * @param url URL to validate hostname
 */ function isValidUrlHostname(url) {
    const hostname = url.hostname;
    if (!hostname || hostname.length > 255) {
        return false;
    }
    if (hostname.startsWith("[") && hostname.endsWith("]") && hostname.includes(":")) {
        // At this point, if it's a valid JS URL, we can assume it's a valid IPv6 address
        return true;
    }
    // Check if it's an IPv4 address
    if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
        return isValidIPv4(hostname);
    }
    // Otherwise, validate as domain name
    return isValidDomainName(hostname);
}
/**
 * Validates IPv4 address format
 * @param ip IPv4 address to validate
 */ function isValidIPv4(ip) {
    const parts = ip.split(".");
    if (parts.length !== 4) {
        return false;
    }
    for (const part of parts){
        const num = parseInt(part, 10);
        if (isNaN(num) || num < 0 || num > 255) {
            return false;
        }
    }
    return true;
}
/**
 * Validates domain name format
 * @param hostname Hostname to validate
 */ function isValidDomainName(hostname) {
    // Handle trailing dot (FQDN) by removing it for validation
    const normalizedHostname = hostname.endsWith(".") ? hostname.slice(0, -1) : hostname;
    // Split into labels and validate each one
    const labels = normalizedHostname.split(".");
    for (const label of labels){
        // Each label must be 1-63 characters
        if (label.length === 0 || label.length > 63) {
            return false;
        }
        // Label cannot start or end with hyphen
        if (label.startsWith("-") || label.endsWith("-")) {
            return false;
        }
        // Label can only contain letters, digits, and hyphens
        if (!/^[a-zA-Z0-9-]+$/.test(label)) {
            return false;
        }
    }
    return true;
}
/**
 * Constructs the folder name for a plan in Microsoft Planner.
 *
 * @param planName - The name of the plan.
 * @param planId - The ID of the plan.
 * @returns The folder name string in the format "{planName}_{sha256hash(planId)}".
 */ export const getPlanFolderNameFromPlanDetails = async (planName, planId)=>{
    const validatedFolderName = generateValidFilename(planName);
    const planGuidHash = await computeSha256Hash(planId, 16);
    return `${validatedFolderName}_${planGuidHash}`;
};
/**
 * Constructs the folder path for a given plan in Microsoft Planner using the plan name and ID.
 *
 * @param planName - The name of the plan.
 * @param planId - The ID of the plan.
 * @returns The folder path string in the format "Microsoft Planner/{planName}_{sha256hash(planId)}".
 */ export const getPlanFolderPathFromPlanDetails = async (planName, planId)=>{
    const planFolderName = await getPlanFolderNameFromPlanDetails(planName, planId);
    return `${PlannerFolderName}/${planFolderName}`;
};
/**
 * Appends a query parameter to a URL if it doesn't already exist
 * @param url The URL to which the parameter will be added
 * @param params The record of parameters to add
 * @returns The URL with the parameter added
 */ export function appendUrlParams(url, params) {
    if (!url || !params || Object.keys(params).length === 0) {
        return url;
    }
    try {
        const urlObj = new URL(url);
        for (const [key, value] of Object.entries(params)){
            if (!urlObj.searchParams.has(key)) {
                urlObj.searchParams.set(key, value);
            }
        }
        return urlObj.toString();
    } catch (e) {
        return url;
    }
}
/**
 * Constructs the URL for a task's comment thread in Outlook
 * @param groupMail - The email address of the group
 * @param postId - The ID of the latest post
 * @returns The constructed URL or undefined if the input is invalid
 */ export function getTaskLegacyCommentThreadUrl(groupMail, postId) {
    const emailParts = groupMail.split("@");
    if (emailParts.length !== 2) {
        return undefined;
    }
    const groupName = encodeURIComponent(emailParts[0]);
    const groupEmailDomain = encodeURIComponent(emailParts[1]);
    if (!groupName || !groupEmailDomain) {
        return undefined;
    }
    const encodedPostId = encodeURIComponent(postId);
    return `https://outlook.cloud.microsoft/mail/deeplink/read/group/${groupEmailDomain}/${groupName}/${encodedPostId}`;
}
